import { trackMixpanelEvent } from './index';

/**
 * Catalogue des événements Mixpanel — basé sur le guide Mai 2026.
 *
 * Convention : snake_case pour les noms d'événements et propriétés
 * (cohérent avec la nomenclature Mixpanel standard).
 */

// ──────────────────────────────────────────────────────────
// Priorité 1 — Essentiels
// ──────────────────────────────────────────────────────────

export const trackUserSignedUp = (props?: { method?: string }): void => {
  trackMixpanelEvent('user_signed_up', props);
};

export const trackUserLoggedIn = (props?: { method?: string }): void => {
  trackMixpanelEvent('user_logged_in', props);
};

export const trackPageViewed = (
  pageName: string,
  props?: { previous_page?: string; time_spent_seconds?: number }
): void => {
  trackMixpanelEvent('page_viewed', { page_name: pageName, ...props });
};

export const trackProfileCompleted = (): void => {
  trackMixpanelEvent('profile_completed');
};

export const trackSearchPerformed = (props: {
  search_term: string;
  results_count?: number;
  search_type?: string;
}): void => {
  trackMixpanelEvent('search_performed', props);
};

export const trackContentShared = (props: {
  content_type: string;
  share_method?: 'email' | 'link' | 'social' | 'whatsapp' | string;
}): void => {
  trackMixpanelEvent('content_shared', props);
};

export const trackFeatureClicked = (props: {
  feature_name: string;
  section?: string;
  is_first_time?: boolean;
}): void => {
  trackMixpanelEvent('feature_clicked', props);
};

// (tree_created : non applicable — pas d'arbre généalogique dans Share Heritage)

// ──────────────────────────────────────────────────────────
// Priorité 2 — Parcours utilisateur
// ──────────────────────────────────────────────────────────

export const trackDemoPopupShown = (): void => {
  trackMixpanelEvent('demo_popup_shown');
};

export const trackDemoPopupBookingClicked = (): void => {
  trackMixpanelEvent('demo_popup_booking_clicked');
};

export const trackDemoPopupSkipped = (): void => {
  trackMixpanelEvent('demo_popup_skipped');
};

export const trackOnboardingStepCompleted = (props: {
  step_number: number;
  step_name: string;
  time_to_complete?: number;
}): void => {
  trackMixpanelEvent('onboarding_step_completed', props);
};

export const trackOnboardingAbandoned = (props: {
  step_number: number;
  step_name?: string;
}): void => {
  trackMixpanelEvent('onboarding_abandoned', props);
};

export const trackDocumentUploaded = (props: {
  file_type?: string;
  file_size?: number;
  context?: string;
}): void => {
  trackMixpanelEvent('document_uploaded', props);
};

export const trackDocumentSuggestionAccepted = (props: {
  document_type?: string;
  edited: boolean;
  bulk: boolean;
  moved: boolean;
}): void => {
  trackMixpanelEvent('document_suggestion_accepted', props);
};

export const trackDocumentSuggestionRejected = (props: { document_type?: string }): void => {
  trackMixpanelEvent('document_suggestion_rejected', props);
};

export const trackReceiptAdded = (props: {
  source: 'camera' | 'upload' | 'existing';
  is_income?: boolean;
  asset_id?: string;
}): void => {
  trackMixpanelEvent('expense_receipt_added', props);
};

export const trackInvitationSent = (props?: {
  method?: string;
  recipients_count?: number;
  role?: string;
}): void => {
  trackMixpanelEvent('invitation_sent', props);
};

export const trackInvitationAccepted = (): void => {
  trackMixpanelEvent('invitation_accepted');
};

export const trackNotificationClicked = (props?: {
  notification_type?: string;
  url?: string;
}): void => {
  trackMixpanelEvent('notification_clicked', props);
};

// ──────────────────────────────────────────────────────────
// Priorité 3 — Conversion et engagement avancé
// ──────────────────────────────────────────────────────────

export const trackErrorEncountered = (props: {
  error_code?: string | number;
  error_message: string;
  page_context?: string;
}): void => {
  trackMixpanelEvent('error_encountered', props);
};

export const trackExportRequested = (props: { export_type: string }): void => {
  trackMixpanelEvent('export_requested', props);
};

export const trackSupportContacted = (props?: { channel?: string }): void => {
  trackMixpanelEvent('support_contacted', props);
};

export const trackSessionEnded = (props?: { duration_seconds?: number }): void => {
  trackMixpanelEvent('session_ended', props);
};

export const trackPracticalInformationFilterApplied = (props: {
  categories: string[];
  count: number;
}): void => {
  trackMixpanelEvent('practical_information_filter_applied', props);
};

export const trackPracticalInfoReminderTimeSet = (props: {
  practical_information_type: string;
  reminder_time_of_day: string;
}): void => {
  trackMixpanelEvent('practical_info_reminder_time_set', props);
};

export const trackPracticalInformationSuggestionClicked = (props: {
  subtype: string;
  type: string;
}): void => {
  trackMixpanelEvent('practical_information_suggestion_clicked', props);
};

export const trackWhatsappShareClicked = (props: {
  source: 'community_feed' | 'project';
  target_kind: 'feedPost' | 'project';
}): void => {
  trackMixpanelEvent('whatsapp_share_clicked', props);
};

export const trackManagerDashboardViewed = (props: {
  has_forecasts: boolean;
  members_count: number;
  overdue_members_count: number;
}): void => {
  trackMixpanelEvent('manager_dashboard_viewed', props);
};

export const trackManagerReminderSent = (props: {
  target_user_id: string;
  amount_due: number;
}): void => {
  trackMixpanelEvent('manager_reminder_sent', props);
};

export const trackAdminTabChanged = (props: { tab: string }): void => {
  trackMixpanelEvent('admin_tab_changed', props);
};

export const trackForecastCategoryExpanded = (props: { category: string }): void => {
  trackMixpanelEvent('forecast_category_expanded', props);
};

export const trackBudgetForecastSectionToggled = (props: {
  nature: 'CHARGE' | 'PRODUCT';
}): void => {
  trackMixpanelEvent('budget_forecast_section_toggled', props);
};

export const trackBudgetKpiBreakdownOpened = (props: {
  metric: 'REALIZED' | 'REMAINING' | 'PROJECTED_OVERRUN';
}): void => {
  trackMixpanelEvent('budget_kpi_breakdown_opened', props);
};

export const trackBudgetSimulatorPerspectiveChanged = (props: {
  perspective: 'house' | 'me';
}): void => {
  trackMixpanelEvent('simulator_perspective_changed', props);
};

export const trackBudgetSimulatorSaved = (props: {
  perspective: 'house' | 'me';
  line_count: number;
  project_count: number;
}): void => {
  trackMixpanelEvent('simulator_saved', props);
};

export const trackHouseholdCreated = (): void => {
  trackMixpanelEvent('household_created');
};

export const trackHouseholdUpdated = (): void => {
  trackMixpanelEvent('household_updated');
};

export const trackHouseholdDeleted = (): void => {
  trackMixpanelEvent('household_deleted');
};

export const trackActivationPromptShown = (props: { asset_id: string }): void => {
  trackMixpanelEvent('activation_prompt_shown', props);
};

export const trackActivationPromptWhatsappClicked = (props: { asset_id: string }): void => {
  trackMixpanelEvent('activation_prompt_whatsapp_clicked', props);
};

export const trackExpensesMarkedReimbursed = (props: {
  expenses_count: number;
  total_amount: number;
  transfers_created: boolean;
}): void => {
  trackMixpanelEvent('expenses_marked_reimbursed', props);
};

export const trackExpenseReceiptOpenedFromList = (props: { count: number }): void => {
  trackMixpanelEvent('expense_receipt_opened_from_list', props);
};

/** A reimbursement recorded against the expenses it settles, not a free-standing transfer. */
export const trackLinkedReimbursementEdited = (props: {
  expenses_count: number;
  total_amount: number;
}): void => {
  trackMixpanelEvent('linked_reimbursement_edited', props);
};

export const trackLinkedReimbursementCreated = (props: {
  expenses_count: number;
  total_amount: number;
}): void => {
  trackMixpanelEvent('linked_reimbursement_created', props);
};

export const trackExpenseSplitByHousehold = (props: {
  split_type: string;
  households_count: number;
  total_amount: number;
}): void => {
  trackMixpanelEvent('expense_split_by_household', props);
};

export const trackExpenseCreated = (props: {
  is_income: boolean;
  is_fund_call: boolean;
  fund_call_payer_set: boolean;
}): void => {
  trackMixpanelEvent('expense_created', props);
};

// ──────────────────────────────────────────────────────────
// Cartography — Structure du bien
// ──────────────────────────────────────────────────────────

export const trackBuildingCreated = (props: { building_type: string }): void => {
  trackMixpanelEvent('cartography_building_created', props);
};

export const trackFloorCreated = (props: { level: number }): void => {
  trackMixpanelEvent('cartography_floor_created', props);
};

export const trackRoomCreated = (props: { room_type: string }): void => {
  trackMixpanelEvent('cartography_room_created', props);
};

export const trackRoomSetOutOfService = (props: { room_type: string }): void => {
  trackMixpanelEvent('cartography_room_set_out_of_service', props);
};

export const trackRoomTypeSearch = (props: {
  query_length: number;
  results_count: number;
}): void => {
  trackMixpanelEvent('room_type_search', props);
};

export const trackSummaryDetailOpened = (props: {
  metric: 'bedrooms' | 'beds' | 'bathrooms' | 'capacity';
}): void => {
  trackMixpanelEvent('summary_detail_opened', props);
};

export const trackRoomActionClicked = (props: {
  action: 'edit' | 'toggle_out_of_service' | 'delete';
}): void => {
  trackMixpanelEvent('room_action_clicked', props);
};

export const trackBedPanelOpened = (props: {
  source: 'pill' | 'row' | 'after_room_create';
}): void => {
  trackMixpanelEvent('bed_panel_opened', props);
};

export const trackBedAdded = (props: { bed_type: string }): void => {
  trackMixpanelEvent('cartography_bed_added', props);
};

export const trackBedRemoved = (): void => {
  trackMixpanelEvent('cartography_bed_removed');
};

// ──────────────────────────────────────────────────────────
// Budget Forecast — Sous-catégories
// ──────────────────────────────────────────────────────────

export const trackBudgetSubcategoryCreated = (props: { sub_category: string }): void => {
  trackMixpanelEvent('budget_subcategory_created', props);
};

export const trackBudgetSubcategoryRenamed = (props: { sub_category: string }): void => {
  trackMixpanelEvent('budget_subcategory_renamed', props);
};

export const trackBudgetSubcategoryDeleted = (): void => {
  trackMixpanelEvent('budget_subcategory_deleted');
};

export const trackRecurrenceOccurrenceAmountSet = (): void => {
  trackMixpanelEvent('recurrence_occurrence_amount_set');
};

export const trackRecurrenceSet = (props: {
  operation_type: 'income' | 'expense';
  frequency: string;
}): void => {
  trackMixpanelEvent('recurrence_set', props);
};

export const trackBudgetCarryoverApplied = (props: {
  lines_count: number;
  indexation_rate: number;
}): void => {
  trackMixpanelEvent('budget_carryover_applied', props);
};

export const trackBudgetOperationExcluded = (props: { main_category: string }): void => {
  trackMixpanelEvent('budget_operation_excluded', props);
};

export const trackBudgetOperationReincluded = (props: { main_category: string }): void => {
  trackMixpanelEvent('budget_operation_reincluded', props);
};

export const trackPlanningDayDetailOpened = (props: { events_count: number }): void => {
  trackMixpanelEvent('planning_day_detail_opened', props);
};

export const trackPlanningFilterGroupToggled = (props: {
  group: 'sejours' | 'maison' | 'reperes' | 'reminders' | 'rooms';
  enabled: boolean;
}): void => {
  trackMixpanelEvent('planning_filter_group_toggled', props);
};

export const trackStayPeriodSelected = (props: {
  source: 'empty_cell' | 'stay_click';
  room_id?: string;
  nights: number;
}): void => {
  trackMixpanelEvent('stay_period_selected', props);
};

export const trackStayRoomConflictShown = (props: {
  room_count: number;
  conflicting_event_count: number;
}): void => {
  trackMixpanelEvent('stay_room_conflict_shown', props);
};

export const trackPlanningViewChanged = (props: {
  view: 'list' | 'month' | 'week' | 'stays';
}): void => {
  trackMixpanelEvent('planning_view_changed', props);
};

export const trackPlanningSharedWhatsapp = (props: { channel: 'whatsapp' | 'copy' }): void => {
  trackMixpanelEvent('planning_shared_whatsapp', props);
};

export const trackPlanningSubscribeClicked = (props: {
  provider: 'apple' | 'google' | 'outlook' | 'copy';
  context: 'app' | 'public';
}): void => {
  trackMixpanelEvent('planning_subscribe_clicked', props);
};

export const trackBankBalanceRecorded = (props: {
  bank_account_id: string;
  amount_sign: 'positive' | 'negative' | 'zero';
}): void => {
  trackMixpanelEvent('bank_balance_recorded', props);
};

export const trackBankBalanceDeleted = (props: { bank_account_id: string }): void => {
  trackMixpanelEvent('bank_balance_deleted', props);
};

export const trackChecklistTemplateUsed = (props: {
  template_key: string;
  mode: 'as_is' | 'customized';
}): void => {
  trackMixpanelEvent('checklist_template_used', props);
};

export const trackPlaceholderMemberCreated = (props: {
  asset_id: string;
  ownership_nature?: string;
  has_estimated_email: boolean;
}): void => {
  trackMixpanelEvent('placeholder_member_created', props);
};

export const trackPlaceholderMemberFused = (props: {
  asset_id: string;
  placeholder_id: string;
  trigger: 'signup_auto' | 'manual_convert';
}): void => {
  trackMixpanelEvent('placeholder_member_fused', props);
};

export const trackPlaceholderMemberDeleted = (props: { asset_id: string }): void => {
  trackMixpanelEvent('placeholder_member_deleted', props);
};

export const trackLinkedEntitiesSectionViewed = (props: {
  source_entity_type: string;
  link_count: number;
  has_business_links: boolean;
  groups_count: number;
}): void => {
  trackMixpanelEvent('linked_entities_section_viewed', props);
};

export const trackLinkedEntityClicked = (props: {
  source_entity_type: string;
  target_entity_type: string;
  link_origin: string;
  trail_depth: number;
}): void => {
  trackMixpanelEvent('linked_entity_clicked', props);
};

export const trackLinkTrailBackClicked = (props: {
  target_entity_type: string;
  steps_back: number;
}): void => {
  trackMixpanelEvent('link_trail_back_clicked', props);
};

export const trackEntityLinkModalOpened = (props: {
  source_entity_type: string;
  initial_target_type: string;
}): void => {
  trackMixpanelEvent('entity_link_modal_opened', props);
};

export const trackEntityLinkCreated = (props: {
  source_entity_type: string;
  target_entity_type: string;
  batch_size: number;
}): void => {
  trackMixpanelEvent('entity_link_created', props);
};

export const trackEntityLinkDeleted = (props: {
  source_entity_type: string;
  target_entity_type: string;
}): void => {
  trackMixpanelEvent('entity_link_deleted', props);
};

export const trackEntityCreateAndLinkClicked = (props: {
  source_entity_type: string;
  new_entity_type: string;
}): void => {
  trackMixpanelEvent('entity_create_and_link_clicked', props);
};

// ──────────────────────────────────────────────────────────
// Feature Preview — Pages de présentation modules non livrés
// ──────────────────────────────────────────────────────────

export const trackFeaturePreviewViewed = (props: { module: string }): void => {
  trackMixpanelEvent('feature_preview_viewed', props);
};

export const trackFeatureSurveyAnswered = (props: {
  module: string;
  question: 'interest' | 'beta_optin' | 'urgency';
  answer: string;
}): void => {
  trackMixpanelEvent('feature_survey_answered', props);
};

// ──────────────────────────────────────────────────────────
// Découvrabilité — carte Livret d'accueil
// ──────────────────────────────────────────────────────────

export const trackBookletCardClicked = (props: { has_booklet: boolean }): void => {
  trackMixpanelEvent('dashboard_booklet_card_clicked', props);
};

export const trackHouseholdsExplainerToggled = (props: { opened: boolean }): void => {
  trackMixpanelEvent('households_explainer_toggled', props);
};

export const trackGovernanceComingSoonClicked = (props: {
  module: 'budget' | 'convention' | 'planning';
}): void => {
  trackMixpanelEvent('governance_coming_soon_clicked', props);
};

// ──────────────────────────────────────────────────────────
// Entre nous — le Tchat
// ──────────────────────────────────────────────────────────

export const trackJournalViewed = (props: { variant: 'compact' | 'full' }): void => {
  trackMixpanelEvent('community_journal_viewed', props);
};

export const trackJournalSeeAllClicked = (): void => {
  trackMixpanelEvent('community_journal_see_all_clicked');
};

export const trackAlbumCreated = (props: { photos_count: number }): void => {
  trackMixpanelEvent('community_album_created', props);
};

export const trackReplySent = (): void => {
  trackMixpanelEvent('community_reply_sent');
};

export const trackCommunityWelcomeDismissed = (): void => {
  trackMixpanelEvent('community_welcome_dismissed');
};

// ──────────────────────────────────────────────────────────
// Finances Home — accueil Finances refondu
// ──────────────────────────────────────────────────────────

export type FinancesTile =
  | 'operations'
  | 'forecast'
  | 'bank_accounts'
  | 'simulator'
  | 'user_balance'
  | 'fund_calls'
  | 'tax_declaration';

export type FinancesAction = 'add_expense' | 'add_income' | 'add_transfer';

export type FinancesComingSoonFeature = 'partner_current_account' | 'tax_declaration' | 'fund_call';

export const trackFinancesHomeViewed = (props: { asset_id: string; year: number }): void => {
  trackMixpanelEvent('finances_home_viewed', props);
};

export const trackFinancesTileClicked = (props: { tile: FinancesTile }): void => {
  trackMixpanelEvent('finances_tile_clicked', props);
};

export const trackFinancesActionClicked = (props: {
  action: FinancesAction;
  source: 'home' | 'operations';
}): void => {
  trackMixpanelEvent('finances_action_clicked', props);
};

export const trackFinancesComingSoonInterested = (props: {
  feature: FinancesComingSoonFeature;
}): void => {
  trackMixpanelEvent('finances_coming_soon_interested', props);
};

// ──────────────────────────────────────────────────────────
// Prévisionnel v2 — « Préparer » / « Suivre »
// ──────────────────────────────────────────────────────────

export const trackBudgetWizardStarted = (props: { asset_id: string; year: number }): void => {
  trackMixpanelEvent('budget_wizard_started', props);
};

export const trackBudgetWizardSaved = (props: {
  asset_id: string;
  year: number;
  lines_count: number;
  expense_total: number;
  income_total: number;
}): void => {
  trackMixpanelEvent('budget_wizard_saved', props);
};

export const trackBudgetTrackingViewed = (props: { asset_id: string; year: number }): void => {
  trackMixpanelEvent('budget_tracking_viewed', props);
};

export const trackBudgetFundCallSuggestionApplied = (props: {
  year: number;
  frequency_per_year: number;
  amount_per_household: number;
}): void => {
  trackMixpanelEvent('budget_fund_call_suggestion_applied', props);
};

export const trackBudgetReportPrinted = (props: { year: number }): void => {
  trackMixpanelEvent('budget_report_printed', props);
};

export const trackBudgetLineDeleted = (props: {
  step: 'fixed_costs' | 'projects' | 'incomes';
}): void => {
  trackMixpanelEvent('budget_line_deleted', props);
};

export const trackStructureViewToggled = (props: { view: 'house' | 'table' }): void => {
  trackMixpanelEvent('structure_view_toggled', props);
};

export const trackHomeCardLinkClicked = (props: {
  card: 'tasks' | 'events' | 'projects' | 'community';
}): void => {
  trackMixpanelEvent('home_card_link_clicked', props);
};

export const trackFundCallCreated = (props: { type: string; lines_count: number }): void => {
  trackMixpanelEvent('fund_call_created', props);
};

export const trackFundCallSent = (props: {
  type: string;
  lines_count: number;
  include_statement: boolean;
}): void => {
  trackMixpanelEvent('fund_call_sent', props);
};

export const trackFundCallLineSettled = (props: { amount: number }): void => {
  trackMixpanelEvent('fund_call_line_settled', props);
};

export const trackFundCallDraftDeleted = (props: { type: string }): void => {
  trackMixpanelEvent('fund_call_draft_deleted', props);
};

export const trackReminderCopied = (props: { source: 'fund_call' | 'expense_share' }): void => {
  trackMixpanelEvent('reminder_copied', props);
};

export const trackMyStatementViewed = (props: { pending_lines: number }): void => {
  trackMixpanelEvent('my_statement_viewed', props);
};

export const trackAccountsClosed = (props: {
  closing_date: string;
  members_count: number;
}): void => {
  trackMixpanelEvent('accounts_closed', props);
};

export const trackBalanceDetailExpenseOpened = (props: { has_settled_shares: boolean }): void => {
  trackMixpanelEvent('balance_detail_expense_opened', props);
};

export const trackSelfStatementOpened = (): void => {
  trackMixpanelEvent('self_statement_opened');
};

export const trackExpenseShareReminderCopied = (): void => {
  trackMixpanelEvent('expense_share_reminder_copied');
};

export const trackAgentMessageSent = (props: { messages_count: number }): void => {
  trackMixpanelEvent('agent_message_sent', props);
};

export const trackAgentContactCreated = (): void => {
  trackMixpanelEvent('agent_contact_created');
};

export const trackAgentContactUpdated = (): void => {
  trackMixpanelEvent('agent_contact_updated');
};

export const trackAgentEventCreated = (props: { is_recurring: boolean }): void => {
  trackMixpanelEvent('agent_event_created', props);
};

export const trackAgentEventUpdated = (): void => {
  trackMixpanelEvent('agent_event_updated');
};

export const trackAgentEventDeleted = (props: { scope: 'occurrence' | 'series' }): void => {
  trackMixpanelEvent('agent_event_deleted', props);
};

export const trackAgentTaskCreated = (): void => {
  trackMixpanelEvent('agent_task_created');
};

export const trackAgentTaskUpdated = (props: { is_done: boolean }): void => {
  trackMixpanelEvent('agent_task_updated', props);
};

export const trackAgentTaskDeleted = (): void => {
  trackMixpanelEvent('agent_task_deleted');
};

export const trackAgentDocumentShown = (props: { is_folder: boolean }): void => {
  trackMixpanelEvent('agent_document_shown', props);
};

export const trackAgentFolderCreated = (): void => {
  trackMixpanelEvent('agent_folder_created');
};

export const trackAgentDocumentsRenamed = (props: { documents_count: number }): void => {
  trackMixpanelEvent('agent_documents_renamed', props);
};

export const trackAgentDocumentsMoved = (props: { documents_count: number }): void => {
  trackMixpanelEvent('agent_documents_moved', props);
};

export type VoiceDictationModule = 'agent' | 'instruction' | 'checklist';

export type VoiceDictationFailureReason =
  | 'permission_denied'
  | 'no_microphone'
  | 'empty'
  | 'too_large'
  | 'unsupported_format'
  | 'too_many'
  | 'server_error';

export const trackVoiceDictationStarted = (props: { module: VoiceDictationModule }): void => {
  trackMixpanelEvent('voice_dictation_started', props);
};

export const trackVoiceDictationCompleted = (props: {
  module: VoiceDictationModule;
  duration_seconds: number;
}): void => {
  trackMixpanelEvent('voice_dictation_completed', props);
};

export const trackVoiceDictationFailed = (props: {
  module: VoiceDictationModule;
  reason: VoiceDictationFailureReason;
  duration_seconds?: number;
}): void => {
  trackMixpanelEvent('voice_dictation_failed', props);
};

export const trackContactCompanyCreated = (props: {
  interlocutors_count: number;
  has_siret: boolean;
  has_usual_payment_mode: boolean;
}): void => {
  trackMixpanelEvent('contact_company_created', props);
};

export const trackContactCompanySearchSelected = (): void => {
  trackMixpanelEvent('contact_company_search_selected');
};

export const trackContactCompanySearchManual = (): void => {
  trackMixpanelEvent('contact_company_search_manual');
};

export const trackContactConvertedToCompany = (props: {
  kept_main_interlocutor: boolean;
}): void => {
  trackMixpanelEvent('contact_converted_to_company', props);
};

export const trackMemoryTimelineViewed = (props: { memory_count: number }): void => {
  trackMixpanelEvent('memory_timeline_viewed', props);
};

export const trackMemoryCreated = (props: {
  kind: string;
  has_voice: boolean;
  drafted_by_lucien: boolean;
  source: 'manual' | 'voice_draft' | 'community';
}): void => {
  trackMixpanelEvent('memory_created', props);
};

export const trackMemoryRecordingAdded = (props: { duration_seconds: number }): void => {
  trackMixpanelEvent('memory_recording_added', props);
};

export const trackMemoryRecordingPlayed = (props: { duration_seconds: number }): void => {
  trackMixpanelEvent('memory_recording_played', props);
};

export const trackMemoryVoiceDraftReceived = (props: {
  has_date: boolean;
  rooms_count: number;
  people_count: number;
}): void => {
  trackMixpanelEvent('memory_voice_draft_received', props);
};

// Inventaire
export const trackInventoryViewed = (props: { item_count: number; is_manager: boolean }): void => {
  trackMixpanelEvent('inventory_viewed', props);
};

export const trackInventoryItemCreated = (props: {
  drafted_by_lucien: boolean;
  has_voice: boolean;
  voice_parts: number;
  photos_count: number;
  invoices_count: number;
  value_source: 'manual' | 'lucien' | 'none';
  created_task: boolean;
  created_memory: boolean;
  seconds_spent: number;
}): void => {
  trackMixpanelEvent('inventory_item_created', props);
};

export const trackInventoryItemExited = (props: { reason: string }): void => {
  trackMixpanelEvent('inventory_item_exited', props);
};

export const trackInventoryItemDeleted = (props: { undone: boolean }): void => {
  trackMixpanelEvent('inventory_item_deleted', props);
};

export const trackInventoryExported = (props: {
  item_count: number;
  value_to_complete_count: number;
  scope: 'house' | 'item';
}): void => {
  trackMixpanelEvent('inventory_exported', props);
};
